/**
 * ApplicantDetail - Individual applicant view with verification data
 *
 * Migrated to shadcn/ui + Hyperkit design system
 */
import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  FileText,
  Camera,
  User,
  Mail,
  Calendar,
  Globe,
  Building2,
  Sparkles,
  ChevronRight,
  ExternalLink,
  Download,
  RefreshCw,
  MessageSquare,
  History,
  Link2,
  Copy,
  Fingerprint,
  Loader2,
  Plus,
  AlertTriangle,
  ArrowUpRight,
  Send
} from 'lucide-react';
import { useApplicant, useApplicantTimeline, useReviewApplicant, useDownloadEvidence, useReverifyApplicant, useApplicantNotes, useAddApplicantNote, useComplianceSummary } from '../hooks/useApplicants';
import { useRunScreening } from '../hooks/useScreening';
import { useApplicantDocuments } from '../hooks/useDocuments';
import { useRiskSummary, useRegenerateRiskSummary } from '../hooks/useAI';
import { useCreateCase } from '../hooks/useCases';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import { useToast } from '../contexts/ToastContext';
import { ApplicantDetailSkeleton } from './shared/LoadingSkeleton';
import { ErrorState, NotFound } from './shared/ErrorState';
import { ConfirmDialog } from './shared';
import DocumentUpload from './DocumentUpload';
import DocumentList from './DocumentList';
import DocumentPreview from './DocumentPreview';
import BiometricVerificationPanel from './biometrics/BiometricVerificationPanel';
import CompliancePanel from './CompliancePanel';
import WalletScreeningPanel from './WalletScreeningPanel';
import RequestDocsModal from './RequestDocsModal';
import { RiskScoreDashboard } from './risk';
import AnalystReviewPanel from './AnalystReviewPanel';
import { Button, Badge, Card } from './ui';
import { cn } from '../lib/utils';

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'ai-agent', label: 'AI Agent' },
  { id: 'screening', label: 'Screening', highlight: true },
  { id: 'identification', label: 'Identification' },
  { id: 'due-diligence', label: 'Due Diligence' },
  { id: 'documents', label: 'Documents' },
  { id: 'activity', label: 'Activity' },
  { id: 'linked', label: 'Linked Items' },
];

const statusConfig = {
  approved: { label: 'Approved', variant: 'success', icon: CheckCircle2 },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
  pending: { label: 'Pending', variant: 'muted', icon: Clock },
  in_progress: { label: 'In Progress', variant: 'warning', icon: Clock },
  review: { label: 'Review', variant: 'info', icon: Clock },
  init: { label: 'New', variant: 'muted', icon: Clock },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const countryFlags = {
  US: '🇺🇸', ZA: '🇿🇦', MX: '🇲🇽', AE: '🇦🇪', GB: '🇬🇧',
  CA: '🇨🇦', AU: '🇦🇺', DE: '🇩🇪', FR: '🇫🇷', JP: '🇯🇵',
  CN: '🇨🇳', IN: '🇮🇳', BR: '🇧🇷', RU: '🇷🇺', KR: '🇰🇷',
  SG: '🇸🇬', HK: '🇭🇰', NG: '🇳🇬', KE: '🇰🇪',
};

const stepIcons = {
  identity: FileText,
  id_document: FileText,
  liveness: Camera,
  selfie: Camera,
  proof_of_address: FileText,
  source_of_funds: FileText,
  screening: Shield,
};

export default function ApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    action: null,
  });

  const [previewDocument, setPreviewDocument] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showRequestDocsModal, setShowRequestDocsModal] = useState(false);

  const { data: applicant, isLoading, error, refetch } = useApplicant(id);
  const { data: documentsResponse } = useApplicantDocuments(id);
  const documents = documentsResponse?.items || [];
  const { data: timeline } = useApplicantTimeline(id);
  const { data: complianceData } = useComplianceSummary(id);
  const { data: riskSummary, isLoading: riskLoading, error: riskError } = useRiskSummary(id);
  const { data: notesData } = useApplicantNotes(id);
  const addNoteMutation = useAddApplicantNote();
  const [noteText, setNoteText] = useState('');

  const createCaseMutation = useCreateCase();
  const reviewMutation = useReviewApplicant();
  const downloadMutation = useDownloadEvidence();
  const regenerateRiskMutation = useRegenerateRiskSummary();
  const rescreenMutation = useRunScreening();
  const reverifyMutation = useReverifyApplicant();

  useKeyboardShortcut('a', () => {
    if (applicant && applicant.status !== 'approved') {
      setConfirmDialog({ isOpen: true, action: 'approve' });
    }
  });
  useKeyboardShortcut('r', () => {
    if (applicant && applicant.status !== 'rejected') {
      setConfirmDialog({ isOpen: true, action: 'reject' });
    }
  });
  useKeyboardShortcut('Escape', () => navigate('/applicants'), { enableOnFormElements: false });

  const handleBack = () => navigate('/applicants');

  const handleApproveClick = () => setConfirmDialog({ isOpen: true, action: 'approve' });
  const handleRejectClick = () => setConfirmDialog({ isOpen: true, action: 'reject' });

  const handleRescreen = async () => {
    if (!applicant) return;
    try {
      await rescreenMutation.mutateAsync({
        applicant_id: applicant.id,
        check_types: ['sanctions', 'pep', 'adverse_media'],
      });
      toast.success('Screening complete');
      refetch();
    } catch (err) {
      toast.error(err.message || 'Screening failed');
    }
  };

  const handleReverify = async () => {
    if (!applicant) return;
    try {
      const result = await reverifyMutation.mutateAsync(applicant.id);
      const checks = result?.checks || [];
      const passed = checks.filter(c => c.status === 'passed').length;
      toast.success(`Re-verification complete: ${passed}/${checks.length} checks passed`);
      refetch();
    } catch (err) {
      toast.error(err.message || 'Re-verification failed');
    }
  };

  const executeReviewAction = async () => {
    const action = confirmDialog.action;
    const previousStatus = applicant?.status;
    setConfirmDialog({ isOpen: false, action: null });

    try {
      await reviewMutation.mutateAsync({
        id,
        decision: action,
        notes: `${action === 'approve' ? 'Approved' : 'Rejected'} via dashboard`,
      });

      toast.success(
        `Applicant ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
        {
          duration: 10000,
          action: previousStatus && previousStatus !== action ? {
            label: 'Undo',
            onClick: async () => {
              try {
                const revertAction = previousStatus === 'approved' ? 'approve' :
                                    previousStatus === 'rejected' ? 'reject' : 'request_info';
                await reviewMutation.mutateAsync({
                  id,
                  decision: revertAction,
                  notes: `Reverted from ${action} to ${previousStatus} via undo`,
                });
                toast.info('Action undone - applicant status reverted');
              } catch (undoErr) {
                toast.error(`Failed to undo: ${undoErr.message}`);
              }
            },
          } : undefined,
        }
      );
    } catch (err) {
      toast.error(`Failed to ${action} applicant: ${err.message}`, {
        action: {
          label: 'Retry',
          onClick: () => setConfirmDialog({ isOpen: true, action }),
        },
      });
    }
  };

  const handleDownloadEvidence = async () => {
    const displayName = applicant?.first_name && applicant?.last_name
      ? `${applicant.first_name}_${applicant.last_name}`
      : `applicant_${id.slice(0, 8)}`;

    try {
      await downloadMutation.mutateAsync({
        id,
        filename: `evidence_${displayName}.pdf`,
      });
      toast.success('Evidence pack downloaded successfully');
    } catch (err) {
      toast.error(`Failed to download evidence: ${err.message}`);
    }
  };

  const handleRegenerateRiskSummary = async () => {
    try {
      await regenerateRiskMutation.mutateAsync(id);
      toast.success('AI risk summary regenerated');
    } catch (err) {
      toast.error(`Failed to regenerate risk summary: ${err.message}`);
    }
  };

  const handleEscalate = async () => {
    if (!applicant) return;
    try {
      await createCaseMutation.mutateAsync({
        applicant_id: applicant.id,
        title: `Escalation: ${displayName}`,
        type: 'verification',
        priority: 'high',
        status: 'escalated',
      });
      toast.success('Case created and escalated');
    } catch (err) {
      toast.error(`Failed to escalate: ${err.message}`);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNoteMutation.mutateAsync({ id, content: noteText.trim() });
      setNoteText('');
      toast.success('Note added');
    } catch (err) {
      toast.error(`Failed to add note: ${err.message}`);
    }
  };

  if (isLoading) return <ApplicantDetailSkeleton />;

  if (error) {
    if (error.message?.includes('404') || error.message?.includes('not found')) {
      return (
        <NotFound
          title="Applicant Not Found"
          message="The applicant you're looking for doesn't exist or has been deleted."
          onBack={handleBack}
          onHome={() => navigate('/')}
        />
      );
    }
    return (
      <ErrorState
        title="Failed to load applicant"
        message="We couldn't load this applicant's details. Please try again."
        error={error}
        onRetry={refetch}
        onBack={handleBack}
      />
    );
  }

  if (!applicant) {
    return (
      <NotFound
        title="Applicant Not Found"
        message="The applicant you're looking for doesn't exist or has been deleted."
        onBack={handleBack}
        onHome={() => navigate('/')}
      />
    );
  }

  const status = statusConfig[applicant.status] || statusConfig.pending;
  const StatusIcon = status?.icon;

  const displayName = applicant.first_name && applicant.last_name
    ? `${applicant.first_name} ${applicant.last_name}`
    : applicant.email?.split('@')[0] || `Applicant ${applicant.id.slice(0, 8)}`;

  const countryCode = applicant.country_code || applicant.nationality?.slice(0, 2)?.toUpperCase();
  const activityItems = timeline || applicant.activity || [];

  return (
    <div className="max-w-[1400px]" data-testid="page-applicant-detail" data-page-ready={!isLoading} data-entity-id={applicant?.id}>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="secondary" size="sm" onClick={handleBack}>
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1 className="text-[28px] font-semibold tracking-tight text-grey-900 dark:text-grey-50">{displayName}</h1>
          <Badge variant={status?.variant} className="gap-1.5 px-3 py-1.5 rounded-full text-[13px]">
            {StatusIcon && <StatusIcon size={14} />}
            {status?.label}
          </Badge>
        </div>

        <div className="flex items-center flex-wrap gap-3 text-[13px] text-grey-500 dark:text-grey-400">
          <span className="flex items-center gap-1.5">
            <Mail size={14} />
            {applicant.email || '—'}
          </span>
          {countryCode && (
            <span className="flex items-center gap-1.5">
              <Globe size={14} />
              {countryFlags[countryCode] || '🌍'} {applicant.nationality || countryCode}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar size={14} />
            Submitted {formatDateTime(applicant.submitted_at || applicant.created_at)}
          </span>
          <span className="font-mono text-xs text-grey-400">
            ID: {applicant.id}
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={handleDownloadEvidence} disabled={downloadMutation.isPending}>
            {downloadMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Export
          </Button>
          <Button variant="secondary" size="sm" onClick={handleRescreen} disabled={rescreenMutation.isPending}>
            {rescreenMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Re-screen
          </Button>
          <Button size="sm" onClick={handleReverify} disabled={reverifyMutation.isPending} title="Re-run biometrics, screening, device intel, and name validation">
            {reverifyMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
            Re-verify All
          </Button>
          <Button variant="warning" size="sm" onClick={handleEscalate} disabled={createCaseMutation.isPending} data-testid="escalate-btn" data-action="escalate" data-entity-id={applicant?.id} title="Escalate to case">
            {createCaseMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
            Escalate
          </Button>
          <Button variant="destructive" size="sm" onClick={handleRejectClick} disabled={reviewMutation.isPending || applicant.status === 'rejected'} aria-label="Reject applicant (R)" title="Reject (R)" data-testid="reject-btn" data-action="reject" data-entity-id={applicant?.id}>
            {reviewMutation.isPending && reviewMutation.variables?.decision === 'reject' ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
            Reject
          </Button>
          <Button variant="success" size="sm" onClick={handleApproveClick} disabled={reviewMutation.isPending || applicant.status === 'approved'} aria-label="Approve applicant (A)" title="Approve (A)" data-testid="approve-btn" data-action="approve" data-entity-id={applicant?.id}>
            {reviewMutation.isPending && reviewMutation.variables?.decision === 'approve' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Approve
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-xl p-1 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={cn(
              'px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5',
              activeTab === tab.id
                ? tab.highlight
                  ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
                  : 'bg-grey-100 dark:bg-grey-800 text-grey-900 dark:text-grey-50'
                : tab.highlight
                  ? 'bg-gradient-to-r from-green-500/10 to-blue-500/10 text-grey-600 dark:text-grey-300 hover:text-grey-900 dark:hover:text-grey-50'
                  : 'text-grey-500 dark:text-grey-400 hover:text-grey-900 dark:hover:text-grey-50'
            )}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.highlight && <Fingerprint size={14} />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="max-w-[1000px]">
        {activeTab === 'overview' && (
          <>
            <RiskScoreDashboard
              applicantId={id}
              onActionClick={(action) => {
                if (action === 'auto_approve') setConfirmDialog({ isOpen: true, action: 'approve' });
                else if (action === 'auto_reject') setConfirmDialog({ isOpen: true, action: 'reject' });
              }}
            />

            {/* Personal Information */}
            <Card className="mb-5">
              <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
                <User size={14} className="text-grey-500" />
                <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Personal Information</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3 gap-x-6">
                  <InfoItem label="Full Name" value={displayName} />
                  <InfoItem label="Email" value={applicant.email} copyable />
                  <InfoItem label="Phone" value={applicant.phone} />
                  <InfoItem label="Date of Birth" value={applicant.date_of_birth} />
                  <InfoItem label="Nationality" value={countryCode ? `${countryFlags[countryCode] || '🌍'} ${applicant.nationality || countryCode}` : null} />
                  {applicant.custom_data?.sdk_flow && (
                    <InfoItem label="Verification Flow" value={applicant.custom_data.sdk_flow} />
                  )}
                  {/* Hide auto-generated test-link external IDs (e.g. "test-live-xxxx") - they aren't meaningful to a reviewer. */}
                  {applicant.external_id && !/^test-/i.test(applicant.external_id) && (
                    <InfoItem label="External ID" value={applicant.external_id} mono />
                  )}
                  {applicant.address && (
                    <div className="col-span-2">
                      <InfoItem
                        label="Address"
                        value={typeof applicant.address === 'object'
                          ? [applicant.address.street, applicant.address.city, applicant.address.state, applicant.address.postal_code, applicant.address.country].filter(Boolean).join(', ')
                          : applicant.address}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Notes & Quick Actions */}
            <div className="grid grid-cols-2 gap-4 mb-5">
              <Card data-testid="notes-section">
                <div className="px-4 py-2.5 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
                  <MessageSquare size={14} className="text-grey-500" />
                  <span className="text-[13px] font-semibold text-grey-900 dark:text-grey-50">Notes</span>
                </div>
                <div className="p-4">
                  <div className="flex gap-2 mb-2.5">
                    <input
                      type="text"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                      placeholder="Add a note..."
                      data-testid="note-input"
                      className="flex-1 px-2.5 py-1.5 rounded-md border border-grey-200 dark:border-grey-700 bg-white dark:bg-grey-900 text-grey-900 dark:text-grey-50 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                    <Button variant="secondary" size="sm" onClick={handleAddNote} disabled={!noteText.trim() || addNoteMutation.isPending} data-testid="add-note-btn" className="px-2.5 py-1.5">
                      <Send size={12} />
                    </Button>
                  </div>
                  {(notesData?.notes || []).length > 0 ? (
                    <div className="max-h-[100px] overflow-y-auto">
                      {[...(notesData.notes || [])].reverse().slice(0, 3).map((note, idx) => (
                        <div key={idx} className={cn('py-1.5', idx < 2 && 'border-b border-grey-200 dark:border-grey-800')}>
                          <div className="text-xs text-grey-900 dark:text-grey-50 mb-0.5">{note.content}</div>
                          <div className="text-[10px] text-grey-400">{note.author?.split('@')[0]} • {new Date(note.created_at).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-grey-400 text-xs text-center py-2">No notes yet</div>
                  )}
                </div>
              </Card>

              <Card>
                <div className="px-4 py-2.5 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
                  <Link2 size={14} className="text-grey-500" />
                  <span className="text-[13px] font-semibold text-grey-900 dark:text-grey-50">Quick Actions</span>
                </div>
                <div className="p-4 space-y-2">
                  <Button variant="secondary" size="sm" className="w-full justify-center text-xs" onClick={handleDownloadEvidence} disabled={downloadMutation.isPending}>
                    {downloadMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    Export Evidence Pack
                  </Button>
                  <Button variant="secondary" size="sm" className="w-full justify-center text-xs" onClick={() => setShowRequestDocsModal(true)}>
                    <FileText size={14} />
                    Request Documents
                  </Button>
                  <Button variant="secondary" size="sm" className="w-full justify-center text-xs" onClick={() => navigate(`/cases?applicant_id=${applicant.id}&create=true`)}>
                    <ExternalLink size={14} />
                    Open Case
                  </Button>
                </div>
              </Card>
            </div>

            {applicant.company_name && (
              <Card className="mb-6">
                <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-grey-500" />
                    <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Company Information</span>
                  </div>
                  <span className="text-teal-500 text-[13px] cursor-pointer hover:underline flex items-center gap-1">
                    View Company <ChevronRight size={14} />
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InfoItem label="Company Name" value={applicant.company_name} />
                    {applicant.company_role && <InfoItem label="Role" value={applicant.company_role} />}
                  </div>
                </div>
              </Card>
            )}

            {/* AI Risk Assessment */}
            <Card className="border-teal-500/50 bg-gradient-to-br from-white to-teal-500/5 dark:from-grey-900 dark:to-teal-500/10">
              <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-teal-500" />
                  <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">AI Risk Assessment</span>
                </div>
                <Button variant="secondary" size="sm" onClick={handleRegenerateRiskSummary} disabled={regenerateRiskMutation.isPending}>
                  {regenerateRiskMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Regenerate
                </Button>
              </div>
              <div className="p-4">
                {riskLoading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-grey-400">
                    <Loader2 size={20} className="animate-spin" />
                    Loading AI analysis...
                  </div>
                ) : riskError ? (
                  <div className="text-center py-10">
                    <AlertTriangle size={32} className="text-red-400 mx-auto mb-3 opacity-70" />
                    <p className="font-medium text-grey-900 dark:text-grey-50 mb-2">AI Analysis Unavailable</p>
                    <p className="text-[13px] text-grey-500 mb-4">{riskError.message || 'Unable to generate AI analysis'}</p>
                    <Button variant="secondary" size="sm" onClick={handleRegenerateRiskSummary} disabled={regenerateRiskMutation.isPending}>
                      {regenerateRiskMutation.isPending ? <><Loader2 size={14} className="animate-spin" /> Retrying...</> : <><RefreshCw size={14} /> Retry</>}
                    </Button>
                  </div>
                ) : riskSummary ? (
                  <>
                    <div
                      className="text-sm leading-relaxed text-grey-900 dark:text-grey-50 whitespace-pre-wrap [&_strong]:text-teal-500"
                      dangerouslySetInnerHTML={{
                        __html: (riskSummary.summary || riskSummary.response || '')
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\n/g, '<br />')
                      }}
                    />
                    {riskSummary.confidence && (
                      <div className="flex items-center gap-2 mt-5 p-3 bg-grey-50 dark:bg-grey-800 rounded-lg">
                        <Sparkles size={14} className="text-teal-500" />
                        <span className="text-[13px]">Confidence</span>
                        <div className="flex-1 h-1.5 bg-grey-200 dark:bg-grey-700 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: `${riskSummary.confidence}%` }} />
                        </div>
                        <span className="text-[13px] font-semibold">{riskSummary.confidence}%</span>
                      </div>
                    )}
                    <div className="mt-5 pt-5 border-t border-grey-200 dark:border-grey-800">
                      <div className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-3">Citations & Sources</div>
                      {riskSummary.citations && riskSummary.citations.length > 0 ? (
                        riskSummary.citations.map((citation, idx) => (
                          <div key={idx} className="grid grid-cols-[90px_180px_1fr] gap-3 p-2.5 bg-grey-50 dark:bg-grey-800 rounded-md mb-2 text-xs items-start">
                            <Badge variant={citation.source_type === 'screening' ? 'destructive' : citation.source_type === 'document' ? 'info' : 'success'} className="text-[10px] uppercase tracking-wide justify-center">
                              {(citation.source_type || citation.type || '').toUpperCase()}
                            </Badge>
                            <span className="font-medium text-grey-900 dark:text-grey-50">{citation.source_name || citation.source}</span>
                            <span className="text-grey-500">{citation.excerpt || citation.detail}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-grey-400 text-[13px]">No citations available for this assessment.</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-10 text-grey-400">
                    <Sparkles size={32} className="mx-auto mb-3 opacity-50" />
                    <p>No AI analysis available yet</p>
                    <Button variant="secondary" size="sm" className="mt-4" onClick={handleRegenerateRiskSummary} disabled={regenerateRiskMutation.isPending}>
                      {regenerateRiskMutation.isPending ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><Sparkles size={14} /> Generate AI Analysis</>}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}

        {activeTab === 'ai-agent' && (
          <AnalystReviewPanel applicantId={id} />
        )}

        {activeTab === 'identification' && (
          <BiometricVerificationPanel
            applicant={applicant}
            documents={documents}
            verificationResults={(() => {
              const vr = applicant.custom_data?.verification_results || {};
              const bv = applicant.custom_data?.biometric_verification || {};
              // The in-flow (mediapipe) result and the AWS Rekognition result both
              // land in custom_data. The Rekognition one (verification_results) is the
              // real score; prefer whichever object actually carries a non-zero result
              // for liveness/face_match so a 0.0 placeholder never wins.
              const pick = (a, b) => {
                if (a && (a.confidence ?? a.similarity ?? 0) > 0) return a;
                if (b && (b.confidence ?? b.similarity ?? 0) > 0) return b;
                return a || b || null;
              };
              return {
                ...vr,
                ...bv,
                liveness: pick(vr.liveness, bv.liveness),
                face_match: pick(vr.face_match, bv.face_match),
                raw: applicant.custom_data?.raw_service_results || {},
              };
            })()}
            session={applicant.custom_data?.session_info || {}}
            signals={applicant.custom_data?.fraud_signals || {}}
            reports={applicant.custom_data?.screening_results || {}}
            compliance={complianceData}
            timing={{
              created_at: applicant.created_at,
              submitted_at: applicant.submitted_at,
              verified_at: applicant.custom_data?.verification_completed_at,
            }}
          />
        )}

        {activeTab === 'screening' && (
          <>
            <CompliancePanel applicantId={id} />
            <div className="mt-4">
              <WalletScreeningPanel applicantId={id} />
            </div>
          </>
        )}

        {activeTab === 'activity' && (
          <Card>
            <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
              <History size={16} className="text-grey-500" />
              <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Activity Timeline</span>
            </div>
            <div className="p-4">
              {activityItems.length > 0 ? (
                <div className="relative before:absolute before:left-[15px] before:top-0 before:bottom-0 before:w-0.5 before:bg-grey-200 dark:before:bg-grey-700">
                  {activityItems.map((item, idx) => (
                    <div key={idx} className="flex gap-4 py-4 relative">
                      <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 border-2',
                        item.type === 'decision' ? 'bg-green-500/15 border-green-500 text-green-500' :
                        item.type === 'review' ? 'bg-blue-500/15 border-blue-500 text-blue-500' :
                        item.type === 'screening' ? 'bg-orange-500/15 border-orange-500 text-orange-500' :
                        'bg-teal-500/15 border-teal-500 text-teal-500'
                      )}>
                        {item.type === 'decision' && <CheckCircle2 size={14} />}
                        {item.type === 'review' && <User size={14} />}
                        {item.type === 'screening' && <Shield size={14} />}
                        {(item.type === 'verification' || !item.type) && <FileText size={14} />}
                        {item.type === 'submission' && <Clock size={14} />}
                      </div>
                      <div className="flex-1 pt-1">
                        <div className="text-sm font-medium text-grey-900 dark:text-grey-50 mb-1">{item.event || item.description || item.action}</div>
                        <div className="text-xs text-grey-400">{item.actor || item.user || 'System'} • {formatDateTime(item.timestamp || item.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-grey-400 text-center py-10">No activity recorded yet</div>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'due-diligence' && (
          <Card>
            <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
              <CheckCircle2 size={16} className="text-grey-500" />
              <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Verification Steps</span>
            </div>
            <div className="p-4">
              {applicant.steps && applicant.steps.length > 0 ? (
                applicant.steps.map((step, idx) => {
                  const StepIcon = stepIcons[step.name] || stepIcons[step.step_name] || FileText;
                  const isComplete = step.status === 'completed' || step.status === 'complete';
                  return (
                    <div key={idx} className={cn('flex items-center gap-3 py-4', idx < applicant.steps.length - 1 && 'border-b border-grey-200 dark:border-grey-800')}>
                      <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        isComplete ? 'bg-green-500/15 text-green-500' : 'bg-grey-100 dark:bg-grey-800 text-grey-400'
                      )}>
                        <StepIcon size={18} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-grey-900 dark:text-grey-50">{step.name || step.step_name}</div>
                        <div className="text-xs text-grey-400">
                          {isComplete ? `Completed ${step.completed_at ? formatDateTime(step.completed_at) : ''}` : step.status}
                        </div>
                      </div>
                      {isComplete && <CheckCircle2 size={18} className="text-green-500" />}
                    </div>
                  );
                })
              ) : (
                <div className="text-grey-400 text-center py-10">No verification steps configured</div>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'documents' && (
          <>
            <Card className="mb-6">
              <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Send size={16} className="text-grey-500" />
                  <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Request Documents</span>
                </div>
                <Button size="sm" onClick={() => setShowRequestDocsModal(true)}>
                  <MessageSquare size={14} />
                  Request Docs
                </Button>
              </div>
              <div className="p-4">
                <p className="text-[13px] text-grey-500">Send a request to the applicant to upload additional documents via email or link.</p>
              </div>
            </Card>

            <Card className="mb-6">
              <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-grey-500" />
                  <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Upload Document</span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowUploadForm(!showUploadForm)}>
                  {showUploadForm ? 'Hide Form' : <><Plus size={14} /> New Upload</>}
                </Button>
              </div>
              {showUploadForm && (
                <div className="p-4">
                  <DocumentUpload applicantId={id} analyze={true} onUploadComplete={() => { setShowUploadForm(false); refetch(); }} />
                </div>
              )}
            </Card>

            <Card>
              <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
                <FileText size={16} className="text-grey-500" />
                <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Uploaded Documents</span>
              </div>
              <div className="p-4">
                <DocumentList applicantId={id} onDocumentClick={(doc) => setPreviewDocument(doc)} showEmpty={true} />
              </div>
            </Card>
          </>
        )}

        {activeTab === 'linked' && (
          <Card>
            <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
              <Link2 size={16} className="text-grey-500" />
              <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">Linked Items</span>
            </div>
            <div className="p-4">
              <div className="text-grey-400 text-center py-10">No linked items found</div>
            </div>
          </Card>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.action === 'approve' ? 'Approve Applicant' : 'Reject Applicant'}
        message={
          confirmDialog.action === 'approve'
            ? `Are you sure you want to approve ${applicant?.full_name || 'this applicant'}? This will complete their verification process.`
            : `Are you sure you want to reject ${applicant?.full_name || 'this applicant'}? This action cannot be undone.`
        }
        confirmLabel={confirmDialog.action === 'approve' ? 'Approve' : 'Reject'}
        variant={confirmDialog.action === 'approve' ? 'success' : 'danger'}
        isLoading={reviewMutation.isPending}
        onConfirm={executeReviewAction}
        onCancel={() => setConfirmDialog({ isOpen: false, action: null })}
      />

      <DocumentPreview document={previewDocument} isOpen={!!previewDocument} onClose={() => setPreviewDocument(null)} />
      <RequestDocsModal isOpen={showRequestDocsModal} onClose={() => setShowRequestDocsModal(false)} applicantId={id} applicantName={applicant?.full_name} />
    </div>
  );
}

function InfoItem({ label, value, copyable, mono }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-grey-400 uppercase tracking-wider">{label}</span>
      <span className={cn('text-[13px] text-grey-900 dark:text-grey-50 flex items-center gap-2', mono && 'font-mono text-[11px]')}>
        {value || '—'}
        {copyable && value && <Copy size={10} className="text-grey-300 hover:text-grey-500 cursor-pointer opacity-0 hover:opacity-100 transition-opacity" />}
      </span>
    </div>
  );
}
